import axios from 'axios';
import { facebookConfig } from './config';

export async function getAuthUrl(redirectUri, connectionId, options = {}) {
  console.log('Facebook env vars:', {
    clientId: process.env.REACT_APP_FACEBOOK_CLIENT_ID,
    hasSecret: !!process.env.REACT_APP_FACEBOOK_CLIENT_SECRET
  });

  const stateObj = {
    service: options.service || 'facebook',
    connectionId: connectionId
  };
  
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: process.env.REACT_APP_FACEBOOK_CLIENT_ID,
    redirect_uri: redirectUri,
    state: btoa(JSON.stringify(stateObj)),
    scope: options.scopes || facebookConfig.requiredScopes,
    ...(options.service === 'instagram_page' && {
      display: 'popup',
      auth_type: 'rerequest'
    }),
    ...(options.extras || {})
  });

  const authUrl = `${facebookConfig.authEndpoint}?${params.toString()}`;
  console.log('Generated Facebook auth URL:', authUrl);
  return authUrl;
}

export async function exchangeCodeForToken(code, redirectUri) {
  try {
    console.log('Starting token exchange process...');
    console.log('Parameters:', { code, redirectUri });

    // Get access token
    const tokenUrl = `https://graph.facebook.com/v21.0/oauth/access_token?${new URLSearchParams({
      client_id: process.env.REACT_APP_FACEBOOK_CLIENT_ID,
      client_secret: process.env.REACT_APP_FACEBOOK_CLIENT_SECRET,
      redirect_uri: redirectUri,
      code: code
    }).toString()}`;

    console.log('Requesting access token...');
    const response = await axios.get(tokenUrl);
    console.log('Token response:', response.data);

    if (!response.data.access_token) {
      throw new Error('No access token received');
    }

    const accessToken = response.data.access_token;

    try {
      // Get pages in parallel with profile
      const [profileResponse, pagesResponse] = await Promise.all([
        axios.get(`https://graph.facebook.com/${facebookConfig.apiVersion}/me`, {
          params: {
            access_token: accessToken,
            fields: 'id,name,email'
          }
        }),
        axios.get(`https://graph.facebook.com/${facebookConfig.apiVersion}/me/accounts`, {
          params: {
            access_token: accessToken,
            fields: 'id,name,access_token,category,picture{url}'
          }
        })
      ]);

      console.log('Profile response:', profileResponse.data);
      console.log('Pages response:', pagesResponse.data);

      return {
        accessToken: accessToken,
        expiresIn: response.data.expires_in,
        userId: profileResponse.data.id,
        userName: profileResponse.data.name,
        userEmail: profileResponse.data.email,
        pages: pagesResponse.data.data.map(page => ({
          id: page.id,
          name: page.name,
          category: page.category,
          accessToken: page.access_token,
          picture: page.picture?.data?.url
        }))
      };
    } catch (error) {
      // If the secondary requests fail, we still want to return the token data
      console.error('Error fetching additional data:', error);
      return {
        accessToken: accessToken,
        expiresIn: response.data.expires_in
      };
    }
  } catch (error) {
    console.error('Error exchanging code for token:', error);
    if (error.response) {
      console.error('Error response data:', error.response.data);
      console.error('Error response status:', error.response.status);
      console.error('Error response headers:', error.response.headers);
    }
    throw error;
  }
}

export async function getPages(accessToken) {
  try {
    const response = await axios.get(`https://graph.facebook.com/${facebookConfig.apiVersion}/me/accounts`, {
      params: {
        access_token: accessToken,
        fields: 'id,name,access_token,category'
      }
    });

    return response.data.data.map(page => ({
      value: page.id,
      label: page.name,
      category: page.category,
      accessToken: page.access_token // This is already a long-lived token
    }));
  } catch (error) {
    console.error('Error fetching Facebook pages:', error);
    throw error;
  }
}

export async function getProfile(accessToken) {
  try {
    const response = await axios.get(`https://graph.facebook.com/${facebookConfig.apiVersion}/me`, {
      params: {
        access_token: accessToken,
        fields: 'id,name,email,picture'
      }
    });

    return {
      id: response.data.id,
      name: response.data.name,
      email: response.data.email,
      picture: response.data.picture?.data?.url
    };
  } catch (error) {
    console.error('Error fetching Facebook profile:', error);
    throw error;
  }
}

// Test the connection
export async function testConnection(credentials) {
  try {
    // Test the connection by getting the page info
    const response = await axios.get(
      'https://graph.facebook.com/v17.0/me/accounts',  // Use full URL
      {
        params: {
          access_token: credentials.accessToken,
          fields: 'id,name,access_token,category,picture{url}'
        }
      }
    );

    if (!response.data.data || response.data.data.length === 0) {
      throw new Error('No Facebook pages found');
    }

    // Get insights for the selected page
    if (credentials.pageId && credentials.pageAccessToken) {
      const insightsResponse = await axios.get(
        `https://graph.facebook.com/v17.0/${credentials.pageId}`,
        {
          params: {
            access_token: credentials.pageAccessToken,
            fields: 'name,followers_count,fan_count'
          }
        }
      );

      return {
        success: true,
        data: {
          pages: response.data.data,
          pageInfo: insightsResponse.data
        }
      };
    }

    return {
      success: true,
      data: {
        pages: response.data.data
      }
    };
  } catch (error) {
    console.error('Facebook test connection error:', error.response?.data || error);
    return {
      success: false,
      error: error.response?.data?.error?.message || error.message
    };
  }
}

const N8N_API_URL = 'https://aai.bigg.co.uk/webhook';

export async function createPost(connection, content) {
  try {
    console.log('Creating Facebook post...', {
      connection,
      content
    });
    
    // Prepare post data
    const post = {
      message: content.content.replace(/<[^>]*>/g, ''), // Strip HTML tags
      status: content.status === 'scheduled' ? 'SCHEDULED' : 'DRAFT',
      scheduled_publish_time: content.scheduled_for 
        ? Math.floor(new Date(content.scheduled_for).getTime() / 1000)
        : null
    };

    console.log('Request details:', {
      url: `${N8N_API_URL}/facebook-create-post`,
      pageId: connection.settings.selectedPageId,
      content: post
    });

    const response = await axios.post(`${N8N_API_URL}/facebook-create-post`, {
      pageId: connection.settings.selectedPageId,
      pageAccessToken: connection.settings.selectedPageAccessToken,
      content: post
    });

    console.log('Post creation response:', response.data);
    
    if (!response.data.id) {
      throw new Error('No post ID returned from Facebook');
    }

    // Return a more complete response object
    return {
      success: true,
      data: {
        ...response.data,
        content: content.content,
        status: content.status,
        scheduled_for: content.scheduled_for,
        channel_id: 'facebook',
        connection_id: connection.id,
        external_id: response.data.id,
        url: `https://facebook.com/${response.data.id}`
      },
      postId: response.data.id,
      permalink: `https://facebook.com/${response.data.id}`
    };
  } catch (error) {
    console.error('Error creating Facebook post:', error.response?.data || error);
    
    if (error.response?.status === 401) {
      throw new Error(
        'Authentication failed: Please reconnect your Facebook account.'
      );
    }

    throw error;
  }
}

// Add this new function to fetch page insights
export async function getPageInsights(pageId, accessToken) {
  try {
    // Get basic page data first
    const pageResponse = await axios.get(
      `https://graph.facebook.com/v21.0/${pageId}`,
      {
        params: {
          access_token: accessToken,
          fields: [
            'followers_count',
            'fan_count',
            'talking_about_count'
          ].join(',')
        }
      }
    );

    // Get insights with the most basic metric
    const insightsResponse = await axios.get(
      `https://graph.facebook.com/v21.0/${pageId}/insights`,
      {
        params: {
          access_token: accessToken,
          metric: 'page_views_total',  // Using just one basic metric that should work
          period: 'day',
          date_preset: 'last_28d'
        }
      }
    );

    console.log('Page Response:', pageResponse.data);
    console.log('Insights Response:', insightsResponse.data);

    // Process insights data
    let pageViews = 0;
    if (insightsResponse.data.data && insightsResponse.data.data[0]) {
      const values = insightsResponse.data.data[0].values || [];
      pageViews = values.reduce((sum, item) => sum + (item.value || 0), 0);
    }

    // Return combined data
    return {
      // Current page stats (these don't require insights permission)
      currentFollowers: pageResponse.data.followers_count || 0,
      currentLikes: pageResponse.data.fan_count || 0,
      peopleTalking: pageResponse.data.talking_about_count || 0,
      
      // Basic insights metric
      pageViews: pageViews,

      // Add daily data for the chart
      dailyData: {
        dates: insightsResponse.data.data[0]?.values.map(item => {
          const date = new Date(item.end_time);
          return `${date.getDate()}/${date.getMonth() + 1}`;
        }).reverse() || [],
        engagement: insightsResponse.data.data[0]?.values.map(item => item.value || 0).reverse() || []
      }
    };
  } catch (error) {
    console.error('Error fetching Facebook insights:', error);
    if (error.response?.data?.error) {
      console.error('Facebook API Error:', error.response.data.error);
      console.error('Full error response:', error.response.data);
    }
    throw error;
  }
} 